import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  TrendingUp, DollarSign, AlertCircle, CheckCircle,
  Download, RefreshCw, Plus, Printer, Building2, X,
} from 'lucide-react';
import { facturesApi, structuresApi, entreprisesApi } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { isReadOnlyFinance } from '../../utils/permissions';
import {
  formatMontant, getStatutBadgeClass, getStatutLabel, MOIS_NOMS,
} from '../../utils/calculs';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function KPI({ label, value, sub, color, icon: Icon }) {
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-2xl font-bold ${color}`}>{value}</div>
          <div className="text-sm text-gray-500 mt-1">{label}</div>
          {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
        </div>
        <Icon size={22} className={`${color} opacity-60`} />
      </div>
    </div>
  );
}

function B2BFactureModal({ structures, onClose, onDone }) {
  const [entreprises, setEntreprises] = useState([]);
  const [entId, setEntId] = useState('');
  const [nouvelle, setNouvelle] = useState({ raison_sociale: '', siren: '', adresse: '', email: '' });
  const [form, setForm] = useState({ structure_id: '', designation: '', montant: '', mois: new Date().getMonth() + 1, annee: new Date().getFullYear() });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { entreprisesApi.getAll().then(({ data }) => setEntreprises(data || [])).catch(() => {}); }, []);
  useEffect(() => { if (structures[0]) setForm(f => (f.structure_id ? f : { ...f, structure_id: structures[0].id })); }, [structures]);

  const submit = async () => {
    setErr(null);
    if (!form.designation || !form.montant) { setErr('Désignation et montant requis.'); return; }
    setSaving(true);
    try {
      let eid = entId;
      if (entId === '__new') {
        if (!nouvelle.raison_sociale) { setErr('Raison sociale de l’entreprise requise.'); setSaving(false); return; }
        const { data } = await entreprisesApi.create({ ...nouvelle, structure_id: form.structure_id });
        eid = data.id;
      }
      if (!eid) { setErr('Choisissez une entreprise.'); setSaving(false); return; }
      await facturesApi.create({
        client_type: 'entreprise', entreprise_id: eid, structure_id: form.structure_id,
        mois: form.mois, annee: form.annee, designation: form.designation,
        montant_base: parseFloat(form.montant) || 0,
      });
      onDone();
    } catch (e) { setErr(e.response?.data?.error || 'Erreur'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/40" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg text-navy-900 flex items-center gap-2"><Building2 size={18} className="text-gold-600" /> Facturer une entreprise</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>
        <div>
          <label className="text-[12px] text-gray-500">Entreprise cliente</label>
          <select value={entId} onChange={e => setEntId(e.target.value)} className="form-select w-full mt-1">
            <option value="">— Choisir —</option>
            {entreprises.map(en => <option key={en.id} value={en.id}>{en.raison_sociale}</option>)}
            <option value="__new">+ Nouvelle entreprise…</option>
          </select>
        </div>
        {entId === '__new' && (
          <div className="space-y-2 p-3 bg-cream-50 rounded-xl">
            <input value={nouvelle.raison_sociale} onChange={e => setNouvelle(n => ({ ...n, raison_sociale: e.target.value }))} placeholder="Raison sociale *" className="form-input w-full text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input value={nouvelle.siren} onChange={e => setNouvelle(n => ({ ...n, siren: e.target.value }))} placeholder="SIREN" className="form-input text-sm" />
              <input value={nouvelle.email} onChange={e => setNouvelle(n => ({ ...n, email: e.target.value }))} placeholder="Email compta" className="form-input text-sm" />
            </div>
            <input value={nouvelle.adresse} onChange={e => setNouvelle(n => ({ ...n, adresse: e.target.value }))} placeholder="Adresse" className="form-input w-full text-sm" />
          </div>
        )}
        <div>
          <label className="text-[12px] text-gray-500">Crèche émettrice</label>
          <select value={form.structure_id} onChange={e => setForm(f => ({ ...f, structure_id: e.target.value }))} className="form-select w-full mt-1">
            {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[12px] text-gray-500">Désignation</label>
          <input value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} placeholder="Ex. Réservation de 2 berceaux — juillet 2026" className="form-input w-full mt-1" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div><label className="text-[12px] text-gray-500">Montant €</label><input type="number" value={form.montant} onChange={e => setForm(f => ({ ...f, montant: e.target.value }))} className="form-input w-full mt-1" /></div>
          <div><label className="text-[12px] text-gray-500">Mois</label><select value={form.mois} onChange={e => setForm(f => ({ ...f, mois: Number(e.target.value) }))} className="form-select w-full mt-1">{MOIS_NOMS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}</select></div>
          <div><label className="text-[12px] text-gray-500">Année</label><input type="number" value={form.annee} onChange={e => setForm(f => ({ ...f, annee: Number(e.target.value) }))} className="form-input w-full mt-1" /></div>
        </div>
        {err && <div className="text-[13px] text-red-500">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="btn-secondary">Annuler</button>
          <button onClick={submit} disabled={saving} className="btn-primary">{saving ? '…' : 'Créer la facture'}</button>
        </div>
      </div>
    </div>
  );
}

export default function FinancePage() {
  const { getApiParams } = useApp();
  const [b2bOpen, setB2bOpen] = useState(false);
  const { user } = useAuth();
  const readOnly = isReadOnlyFinance(user?.role);
  const [structures, setStructures] = useState([]);
  const [fecStructure, setFecStructure] = useState('');
  useEffect(() => { structuresApi.getAll().then(({ data }) => { setStructures(data || []); if (data && data[0]) setFecStructure(String(data[0].id)); }).catch(() => {}); }, []);
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [factures, setFactures] = useState([]);
  const [arrhes, setArrhes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('');
  const [filterMois, setFilterMois] = useState('');
  const [filterAnnee, setFilterAnnee] = useState(String(new Date().getFullYear()));
  const [generating, setGenerating] = useState(false);
  const currentYear = new Date().getFullYear();

  const loadStats = useCallback(() => {
    const params = { ...getApiParams(), annee: filterAnnee };
    facturesApi.getStats(params)
      .then(res => setStats(res.data))
      .catch(console.error);
  }, [getApiParams, filterAnnee]);

  const loadFactures = useCallback(() => {
    const params = {
      ...getApiParams(),
      ...(filterStatut && { statut: filterStatut }),
      ...(filterMois && { mois: filterMois }),
      ...(filterAnnee && { annee: filterAnnee }),
    };
    facturesApi.getAll(params)
      .then(res => setFactures(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getApiParams, filterStatut, filterMois, filterAnnee]);

  const loadArrhes = useCallback(() => {
    facturesApi.getArrhes()
      .then(res => setArrhes(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    loadStats();
    loadFactures();
    loadArrhes();
  }, [loadStats, loadFactures, loadArrhes]);

  const handleStatut = async (id, statut, mode) => {
    try {
      await facturesApi.updateStatut(id, statut, mode);
      loadFactures();
      loadStats();
    } catch (err) {
      alert('Erreur');
    }
  };

  const [validating, setValidating] = useState(null);
  const handleValider = async (f) => {
    if (!window.confirm(`Valider définitivement la facture de ${f.enfant_prenom} ${f.enfant_nom} (${formatMontant(f.montant_total)}) ?\n\n• Un numéro officiel sera figé et les montants verrouillés.\n• La facture sera envoyée automatiquement par email au parent.`)) return;
    setValidating(f.id);
    try {
      const { data } = await facturesApi.valider(f.id);
      const e = data.envoi || {};
      if (e.statut === 'envoye') alert(`Facture ${data.numero} validée et envoyée à ${e.to}.`);
      else if (e.statut === 'non_envoye') alert(`Facture ${data.numero} validée.\nEnvoi email en attente : ${e.erreur}`);
      else alert(`Facture ${data.numero} validée.\n⚠️ Email non envoyé : ${e.erreur || ''}`);
      loadFactures();
      loadStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la validation');
    } finally { setValidating(null); }
  };

  const handleGenerate = async () => {
    const mois = new Date().getMonth() + 1;
    const annee = currentYear;
    setGenerating(true);
    try {
      const res = await facturesApi.generateMonthly(mois, annee);
      alert(res.data.message);
      loadFactures();
      loadStats();
    } catch (err) {
      alert('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const chartData = stats?.monthlyData?.map(d => ({
    mois: MOIS_NOMS[d.mois - 1]?.slice(0, 3),
    facturé: Math.round(d.total || 0),
    encaissé: Math.round(d.encaisse || 0),
  })) || [];

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord' },
    { id: 'facturation', label: 'Facturation' },
    { id: 'arrhes', label: 'Arrhes & Frais' },
    { id: 'export', label: 'Export' },
  ];

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Year filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Année :</span>
        <select value={filterAnnee} onChange={e => setFilterAnnee(e.target.value)} className="form-select w-28 text-sm">
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Dashboard Tab */}
      {tab === 'dashboard' && (
        <div className="space-y-5">
          {stats && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI label="CA annuel facturé" value={formatMontant(stats.totalFacture)} color="text-gray-900" icon={DollarSign} />
                <KPI label="Encaissé" value={formatMontant(stats.totalEncaisse)} color="text-green-600"
                  sub={`Taux : ${stats.tauxRecouvrement}%`} icon={CheckCircle} />
                <KPI label="En retard" value={formatMontant(stats.totalRetard)} color="text-red-600" icon={AlertCircle} />
                <KPI label="Mois en cours" value={formatMontant(stats.moisEnCours)} color="text-primary-600" icon={TrendingUp} />
              </div>

              {chartData.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="card p-5">
                    <h3 className="font-semibold text-gray-700 mb-4">CA mensuel {filterAnnee}</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}€`} />
                        <Tooltip formatter={v => [`${v}€`]} />
                        <Legend />
                        <Bar dataKey="facturé" fill="#1e2d4a" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="encaissé" fill="#22c55e" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="card p-5">
                    <h3 className="font-semibold text-gray-700 mb-4">Évolution encaissements</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}€`} />
                        <Tooltip formatter={v => [`${v}€`]} />
                        <Line type="monotone" dataKey="facturé" stroke="#1e2d4a" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="encaissé" stroke="#22c55e" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {stats.byStructure && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {stats.byStructure.map(s => (
                    <div key={s.structure_nom} className="card p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.couleur }} />
                        <span className="font-medium text-gray-700">{s.structure_nom}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <div>
                          <div className="text-xl font-bold text-gray-900">{formatMontant(s.total)}</div>
                          <div className="text-xs text-gray-400">{s.nb_factures} facture(s)</div>
                        </div>
                        <div className="text-right">
                          <div className="text-green-600 font-medium">{formatMontant(s.encaisse)}</div>
                          <div className="text-xs text-gray-400">encaissé</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Facturation Tab */}
      {tab === 'facturation' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} className="form-select w-36 text-sm">
                <option value="">Tous statuts</option>
                <option value="en_attente">En attente</option>
                <option value="paye">Payé</option>
                <option value="retard">En retard</option>
              </select>
              <select value={filterMois} onChange={e => setFilterMois(e.target.value)} className="form-select w-36 text-sm">
                <option value="">Tous mois</option>
                {MOIS_NOMS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Export FEC (Fichier des Écritures Comptables) — pour le comptable */}
              <div className="flex items-center gap-1.5 bg-white border border-cream-300 rounded-lg px-2 py-1">
                <span className="text-xs text-navy-400">Écritures :</span>
                <select value={fecStructure} onChange={e => setFecStructure(e.target.value)} className="text-sm bg-transparent outline-none max-w-[160px]">
                  {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                </select>
                <a
                  href={fecStructure ? facturesApi.getFecUrl(fecStructure, filterAnnee) : '#'}
                  className="px-2 py-1 text-xs rounded bg-navy-800 hover:bg-navy-700 text-white font-medium flex items-center gap-1"
                  title={`Export FEC ${filterAnnee} pour cette crèche (factures validées)`}
                >
                  <Download size={13} /> Export FEC
                </a>
              </div>
              {!readOnly && (
                <button onClick={() => setB2bOpen(true)} className="btn-secondary">
                  <Building2 size={15} /> Facturer une entreprise
                </button>
              )}
              {!readOnly && (
                <button onClick={handleGenerate} disabled={generating} className="btn-primary">
                  <RefreshCw size={15} className={generating ? 'animate-spin' : ''} />
                  Générer factures du mois
                </button>
              )}
              {b2bOpen && (
                <B2BFactureModal
                  structures={structures}
                  onClose={() => setB2bOpen(false)}
                  onDone={() => { setB2bOpen(false); loadFactures(); loadStats(); }}
                />
              )}
            </div>
          </div>

          <div className="card overflow-hidden">
            {loading ? (
              <div className="py-16 text-center text-gray-400">Chargement...</div>
            ) : factures.length === 0 ? (
              <div className="py-16 text-center">
                <DollarSign size={40} className="text-gray-300 mx-auto mb-3" />
                <div className="text-gray-400 text-sm">Aucune facture</div>
                <button onClick={handleGenerate} className="btn-primary mt-4">
                  <Plus size={15} /> Générer les factures du mois
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Enfant</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Structure</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Période</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Montant</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Statut</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Paiement</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {factures.map((f, i) => (
                      <tr key={f.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 !== 0 ? 'bg-gray-50/30' : ''}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {f.enfant_prenom} {f.enfant_nom}
                          {f.numero && <div className="text-[11px] text-gray-400 font-normal">N° {f.numero}</div>}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{f.structure_nom}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {MOIS_NOMS[(f.mois || 1) - 1]} {f.annee}
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-800">
                          {formatMontant(f.montant_total)}
                          {f.heures_supp > 0 && (
                            <span className="text-xs text-orange-500 ml-1">+{f.heures_supp}h supp</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={getStatutBadgeClass(f.statut)}>{getStatutLabel(f.statut)}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {f.date_paiement && <div>{f.date_paiement}</div>}
                          {f.mode_paiement && <div>{f.mode_paiement}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <a href={facturesApi.getPdfUrl(f.id)} target="_blank" rel="noopener noreferrer"
                              className="p-1.5 rounded hover:bg-gold-50 text-gray-500 hover:text-gold-600" title={f.date_validation ? 'PDF Factur-X (XML EN 16931 embarqué)' : 'PDF de la facture'}>
                              <Printer size={15} />
                            </a>
                            {f.date_validation && (
                              <a href={facturesApi.getFacturxUrl(f.id)}
                                className="px-1.5 py-1 rounded text-[10px] font-semibold text-gray-400 hover:bg-gray-100 hover:text-navy-600" title="Télécharger le XML Factur-X (EN 16931)">
                                XML
                              </a>
                            )}
                            {!f.date_validation ? (
                              !readOnly && (
                                <button
                                  onClick={() => handleValider(f)}
                                  disabled={validating === f.id}
                                  className="px-2 py-1 text-xs rounded bg-navy-800 hover:bg-navy-700 text-white font-medium disabled:opacity-50"
                                  title="Figer le numéro officiel, verrouiller et envoyer par email"
                                >
                                  {validating === f.id ? '…' : 'Valider & envoyer'}
                                </button>
                              )
                            ) : (
                              <span
                                className={`px-2 py-1 text-xs rounded ${f.envoi_statut === 'echec' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}
                                title={`Validée${f.envoi_statut === 'envoye' ? ' · envoyée par email' : f.envoi_statut === 'echec' ? ' · envoi à refaire' : ' · à envoyer (SMTP)'}`}
                              >
                                ✓ Validée{f.envoi_statut === 'envoye' ? ' · envoyée' : ''}
                              </span>
                            )}
                            {!readOnly && f.statut !== 'paye' && (
                              <button
                                onClick={() => handleStatut(f.id, 'paye', 'Virement')}
                                className="px-2 py-1 text-xs rounded bg-green-100 hover:bg-green-200 text-green-700 font-medium"
                              >
                                Marquer payé
                              </button>
                            )}
                            {!readOnly && f.statut === 'en_attente' && (
                              <button
                                onClick={() => handleStatut(f.id, 'retard')}
                                className="px-2 py-1 text-xs rounded bg-red-100 hover:bg-red-200 text-red-700"
                              >
                                Retard
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Arrhes Tab */}
      {tab === 'arrhes' && (
        <div className="card overflow-hidden">
          {arrhes.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <DollarSign size={40} className="text-gray-300 mx-auto mb-3" />
              Aucun enregistrement d'arrhes
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Enfant</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Structure</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Montant</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Date versement</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Statut</th>
                </tr>
              </thead>
              <tbody>
                {arrhes.map((a, i) => (
                  <tr key={a.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 !== 0 ? 'bg-gray-50/30' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {a.enfant_prenom} {a.enfant_nom}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{a.structure_nom}</td>
                    <td className="px-4 py-3 font-semibold text-primary-600">{formatMontant(a.montant)}</td>
                    <td className="px-4 py-3 text-gray-600">{a.date_versement || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={getStatutBadgeClass(a.statut)}>{getStatutLabel(a.statut)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Export Tab */}
      {tab === 'export' && (
        <div className="card p-6 max-w-xl">
          <h3 className="font-semibold text-gray-700 mb-4">Export comptable</h3>
          <p className="text-sm text-gray-500 mb-5">
            Exportez toutes les factures au format CSV pour votre comptable.
          </p>
          <div className="space-y-4">
            <div>
              <label className="form-label">Année</label>
              <select value={filterAnnee} onChange={e => setFilterAnnee(e.target.value)} className="form-select w-40">
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <a
              href={facturesApi.getExportUrl({ annee: filterAnnee })}
              className="btn-primary inline-flex"
            >
              <Download size={16} /> Télécharger CSV {filterAnnee}
            </a>
            <div className="text-xs text-gray-400 mt-2">
              Le fichier CSV contient : structure, enfant, mois, heures, montants, statut, mode de paiement.
              Encodage UTF-8 avec BOM pour Excel.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
